/**
 * @fileoverview Shared page shell for the ArcMosaico auth screens (AR-792)
 * @description Hero wash + dot grid, Inter typography, pill buttons and input
 * focus rings — values extracted from the arcmosaico.ai theme CSS (main.css).
 * Only rendered when the arc flag is on.
 */

import styled, { keyframes } from 'styled-components'
import { ARC_FONT_FAMILY, ARC_HERO_WASH } from '../../themes/ArcTheme'

// Re-export for the existing auth-screen consumers (canonical home: themes/ArcTheme)
export { ARC_FONT_FAMILY }

/** Palette extracted from the arcmosaico.ai theme CSS */
export const ARC_PURPLE = '#5B4FEB'
export const ARC_PURPLE_HOVER = '#4A3FD4'
export const ARC_PURPLE_TINT = '#F0EFFE'
export const ARC_NAVY = '#0F1035'
export const ARC_BACKGROUND = '#F7F8FC'
export const ARC_BORDER = '#E8E8F0'
export const ARC_TEXT_MUTED = '#555'
export const ARC_INPUT_BORDER = '#E0E0E8'
export const ARC_INPUT_BG = '#FAFAFA'
export const ARC_CARD_RADIUS_PX = 24
export const ARC_CARD_SHADOW = '0 8px 60px rgba(91, 79, 235, 0.1)'

/** Buttons on arcmosaico.ai are pills (.btn { border-radius: 100px }) */
const BUTTON_RADIUS_PX = 100
/** Compact uniform height so the auth screens fit laptop viewports */
const BUTTON_HEIGHT_PX = 44
const FOCUS_RING = '0 0 0 3px rgba(91, 79, 235, 0.1)'
const ENTER_ANIMATION_MS = 300
const BUTTON_HOVER_SHADOW = '0 8px 28px rgba(91, 79, 235, 0.35)'

/** Hero wash lives in themes/ArcTheme (shared with the ArcNewProject flow) */
const HERO_WASH = ARC_HERO_WASH
/** Peach dot grid overlaying the hero on the site */
const DOT_GRID_COLOR = '#FFBFA3'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`

/** Card entrance used by the auth cards (grafted from the HIG proposal) */
export const cardEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: none;
  }
`

export const ArcAuthPage = styled.div`
  min-height: 100vh;
  position: relative;
  isolation: isolate;
  background-color: ${ARC_BACKGROUND};
  background-image: ${HERO_WASH};
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} ${ENTER_ANIMATION_MS}ms ease-out;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: -1;
    background-image: radial-gradient(circle, ${DOT_GRID_COLOR} 1px, transparent 1px);
    background-size: 40px 40px;
    opacity: 0.18;
  }

  font-family: ${ARC_FONT_FAMILY};

  h1,
  h2,
  .mantine-Title-root {
    font-weight: 800;
    letter-spacing: -1px;
    color: ${ARC_NAVY};
  }

  .mantine-Button-root {
    border-radius: ${BUTTON_RADIUS_PX}px;
    height: ${BUTTON_HEIGHT_PX}px;
    font-weight: 700;
    font-size: 15px;
    letter-spacing: -0.2px;
    transition: all 0.2s;
  }

  /* Site .btn hover lift; colors come from the scoped Mantine primary (ArcAuthShell) */
  .mantine-Button-root:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: ${BUTTON_HOVER_SHADOW};
  }

  .mantine-Button-root:not(:disabled):active {
    transform: translateY(0);
    box-shadow: none;
  }

  /* Global theme pins Anchor to the legacy purple via defaultProps; re-pin to arc */
  .mantine-Anchor-root {
    color: ${ARC_PURPLE};
    font-weight: 600;
    transition: color 0.2s;

    &:hover {
      color: ${ARC_PURPLE_HOVER};
    }
  }

  .mantine-InputWrapper-label {
    font-size: 13px;
    font-weight: 700;
    color: #333;
    margin-bottom: 6px;
  }

  .mantine-Input-input {
    border: 1.5px solid ${ARC_INPUT_BORDER};
    border-radius: 10px;
    background-color: ${ARC_INPUT_BG};
    transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;

    &:focus,
    &:focus-within {
      border-color: ${ARC_PURPLE};
      background-color: #fff;
      box-shadow: ${FOCUS_RING};
    }
  }

  a:focus-visible,
  button:focus-visible {
    outline: 2px solid ${ARC_PURPLE};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    & * {
      animation: none !important;
      transition: none !important;
    }
  }
`
